import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  SkipSelf,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { SubSink } from 'subsink';
import { EmailValidators } from '../../validators/email-validators';

type EmailRecipientsForm = {
  firstContact: FormControl<string>;
  remainingContacts: FormControl<string>;
};

@Component({
  selector: 'payment-receipt-recipients',
  templateUrl: './payment-receipt-recipients.component.html',
  styleUrls: ['./payment-receipt-recipients.component.scss'],
})
export class PaymentReceiptRecipientsComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() controlName: string;
  @Input() disabled: boolean;
  @Input() displayPrimaryEmail: boolean;
  @Input() isRemainingContactsRequired = false;

  @Output() receiptRecipientsFormStatusChanged = new EventEmitter<boolean>();

  form: FormGroup<EmailRecipientsForm>;
  firstContactEmail: string;

  private firstContactControl = this.formBuilder.control('', {
    updateOn: 'blur',
    validators: [EmailValidators.emailValidator()],
  });

  private remainingContactsControl = this.formBuilder.control('', {
    updateOn: 'blur',
    validators: [EmailValidators.emailValidator()],
  });

  private control: AbstractControl;
  private subSink = new SubSink();

  constructor(
    @SkipSelf()
    private controlContainer: ControlContainer,
    private formBuilder: FormBuilder,
  ) {
    this.form = this.formBuilder.group<EmailRecipientsForm>({
      firstContact: this.firstContactControl,
      remainingContacts: this.remainingContactsControl,
    });
  }

  ngOnInit(): void {
    this.control = this.controlContainer.control.get(this.controlName);
    this.setControlsDisabledStatus();
    this.initializeContacts();
    this.updateControlValueOnChanges();

    if (this.firstContactControl.value !== '') {
      this.firstContactControl.markAsTouched();
    }

    if (this.remainingContactsControl.value !== '') {
      this.remainingContactsControl.markAsTouched();
    }

    this.form.statusChanges.subscribe((status) => {
      this.receiptRecipientsFormStatusChanged.emit(status === 'VALID');
    });

    this.receiptRecipientsFormStatusChanged.emit(this.form.valid);
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.displayPrimaryEmail) {
      const firstContactControl = this.form.get('firstContact');
      if (changes.displayPrimaryEmail.currentValue === true) {
        firstContactControl.addValidators([EmailValidators.emailValidator()]);
      } else {
        firstContactControl.clearValidators();
      }
      firstContactControl.updateValueAndValidity();
      if (firstContactControl.value !== '') {
        firstContactControl.markAsTouched();
      }
    }

    if (changes.isRemainingContactsRequired) {
      const remainingContactsControl = this.form.get('remainingContacts');
      if (changes.isRemainingContactsRequired.currentValue === true) {
        remainingContactsControl.addValidators([
          Validators.required,
          EmailValidators.emailValidator(),
        ]);
      } else {
        remainingContactsControl.clearValidators();
        remainingContactsControl.addValidators(
          EmailValidators.emailValidator(),
        );
      }
      remainingContactsControl.updateValueAndValidity();
      if (remainingContactsControl.value !== '') {
        remainingContactsControl.markAsTouched();
      }
    }
  }

  private initializeContacts() {
    const value = this.control.value as string[];

    if (!value || value.length == 0) {
      return;
    }

    this.firstContactEmail = this.displayPrimaryEmail ? value[0] || '' : '';
    this.form.controls.firstContact.setValue(this.firstContactEmail);

    const startIndex = this.displayPrimaryEmail ? 1 : 0;

    if (value.length > startIndex) {
      this.form.controls.remainingContacts.setValue(
        value.slice(startIndex).toString(),
      );
    }
  }

  private updateControlValueOnChanges() {
    const subscription = this.remainingContactsControl.valueChanges.subscribe(
      (value: string) => {
        const contacts = value
          .split(',')
          .map((v) => v.trim())
          .filter((c) => c !== '');

        if (this.firstContactEmail !== '' && this.firstContactEmail != null) {
          contacts.unshift(this.firstContactEmail);
        }

        this.control.setValue(contacts);
      },
    );

    this.subSink.add(subscription);
  }

  private setControlsDisabledStatus() {
    if (this.disabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
